import http, { cloudWebBaseURL } from '../utils/request'

/**
 * 任务/流程接口 — 对应后端 TaskController (/api/task)
 */

// 各类任务数量统计（待办/已发起/已处理/可阅/共享/退回/代理/草稿）
export const getTaskCounts = () => http.get('/task/counts')

// 分页请求体：{ pageNumber, pageSize, searchText, sortName, sortOrder }
export const getTodoTask = (req) => http.post('/task/todo', req)
export const getAlreadyInitiated = (req) => http.post('/task/initiated', req)
export const getAlreadyProcessed = (req) => http.post('/task/processed', req)
export const getShareTask = (req) => http.post('/task/share', req)
export const getReturnTask = (req) => http.post('/task/return', req)
export const getAlreadyAgent = (req) => http.post('/task/agent', req)
export const getAlreadyCC = (req) => http.post('/task/cc', req)
export const getAllowViewTask = (req) => http.post('/task/allowview', req)
export const getManageTask = (req) => http.post('/task/managetask', req)

/** 任务管理操作：删除/暂停/恢复/指派/退回重填/指定结束 */
export const taskAdminAction = (payload) => http.post('/task/admin', {
  TaskId: payload.taskId,
  StepId: payload.stepId || 0,
  Action: payload.action,
  TargetAccount: payload.targetAccount || '',
  Memo: payload.memo || '',
  WithdrawStepId: payload.withdrawStepId || '',
  JumpNodeId: payload.jumpNodeId || ''
})

export const getWfsStartProcesses = (searchText = '') => http.get('/task/wfsstart', { params: { searchText } })
export const getDrafts = (req) => http.post('/task/drafts', req)
export const deleteDrafts = (guids) => http.post('/task/drafts/delete', JSON.stringify(guids), {
  headers: { 'Content-Type': 'application/json' }
})

// 统一分页请求构造
export const buildPagedRequest = ({ pageNumber = 0, pageSize = 20, searchText = '', sortName = '', sortOrder = 'desc' } = {}) => ({
  pageNumber, pageSize, searchText, sortName, sortOrder
})

// 构建任务管理请求
export const buildTaskManageRequest = ({
  pageNumber = 0, pageSize = 20, sortName = '', sortOrder = 'desc',
  processName, taskNo, summary, priority, serialNo,
  initiator, initiateTimeStart, initiateTimeEnd, status
} = {}) => ({
  pageNumber, pageSize, sortName, sortOrder,
  processName: processName || undefined,
  taskNo: taskNo || undefined,
  summary: summary || undefined,
  priority: priority || undefined,
  serialNo: serialNo || undefined,
  initiator: initiator || undefined,
  initiateTimeStart: initiateTimeStart || undefined,
  initiateTimeEnd: initiateTimeEnd || undefined,
  status: status || undefined
})

/**
 * 获取流程发起元数据（包含表单信息、设计器类型）
 * @param {string} flowGuid - 流程GUID
 */
export const getStartMeta = (flowGuid) => http.get('/task/flow/meta', { params: { flowGuid } })

/**
 * 发起任务
 * @param {Object} payload - { flowGuid, formData, priority, summary }
 */
export const startTask = (payload) => http.post('/task/flow/start', {
  FlowGuid: payload.flowGuid,
  FormData: typeof payload.formData === 'string' ? payload.formData : JSON.stringify(payload.formData || {}),
  Memo: payload.memo || '',
  Priority: payload.priority === 2 ? '高' : payload.priority === 1 ? '中' : '低',
  Description: payload.summary || ''
})

/**
 * 提交任务
 * @param {Object} payload - { taskId, formData, comment, action, nextApprover }
 */
export const submitTask = (payload) => http.post('/task/flow/action', {
  TaskId: payload.taskId,
  StepId: payload.stepId || 0,
  Action: payload.action || 'agree',
  Memo: payload.memo || payload.comment || '',
  FormData: typeof payload.formData === 'string' ? payload.formData : JSON.stringify(payload.formData || {})
})

/**
 * 审批任务（通过/拒绝）
 * @param {Object} payload - { taskId, action, comment, nextApprover, formData, stepId, actionLinkName, actionLinkId }
 */
export const approveTask = (payload) => http.post('/task/flow/action', {
  TaskId: payload.taskId,
  StepId: payload.stepId || 0,
  Action: payload.action === 'approve' ? 'agree' : payload.action === 'reject' ? 'reject' : payload.action,
  ActionLinkName: payload.actionLinkName || '',
  ActionLinkId: payload.actionLinkId || 0,
  Memo: payload.memo || payload.comment || '',
  FormData: typeof payload.formData === 'string' ? payload.formData : JSON.stringify(payload.formData || {})
})

/**
 * 转办任务（委托给目标用户处理）
 * @param {Object} payload - { taskId, stepId, targetAccount, comment, formData }
 */
export const forwardTask = (payload) => http.post('/task/flow/action', {
  TaskId: payload.taskId,
  StepId: payload.stepId || 0,
  Action: 'forward',
  TargetAccount: payload.targetAccount || '',
  Memo: payload.memo || payload.comment || '',
  FormData: typeof payload.formData === 'string' ? payload.formData : JSON.stringify(payload.formData || {})
})

/**
 * 取回任务（从已处理/已提交任务取回到本人）
 * @param {Object} payload - { taskId, stepId }
 */
export const retrieveTask = (payload) => http.post('/task/flow/action', {
  TaskId: payload.taskId,
  StepId: payload.stepId || 0,
  Action: 'retrieve'
})

/**
 * 保存草稿
 * @param {Object} payload - { taskId, formData }
 */
export const saveDraft = (payload) => http.post('/task/flow/draft/save', {
  FlowGuid: payload.flowGuid || '',
  Name: payload.summary || '',
  FormData: typeof payload.formData === 'string' ? payload.formData : JSON.stringify(payload.formData || {}),
  DraftGuid: payload.draftGuid || ''  // 更新已有草稿时传入
})

/**
 * 加载草稿数据
 * @param {string} draftGuid - 草稿GUID
 */
export const loadDraft = (draftGuid) => http.get('/task/flow/draft/load', { params: { draftGuid } })

/**
 * 获取任务详情及表单数据
 * @param {string} taskId - 任务ID
 */
export const getTaskDetail = (taskId) => http.get(`/task/detail/${taskId}`)

/**
 * 获取任务审批历史
 * @param {string} taskId - 任务ID
 */
export const getTaskHistory = (taskId) => http.get(`/task/history/${taskId}`)

/**
 * 获取当前节点的操作按钮配置（从 SYSBPMDLINK 表读取）
 * @param {number} taskId - 任务ID
 * @param {number} stepId - 步骤ID
 */
export const getButtonConfig = (taskId, stepId) => http.get('/task/flow/buttons', { params: { taskId, stepId } })

/**
 * 获取任务打印数据（打印模版 + 业务数据 + 审批历史）
 * @param {string|number} taskId - 任务ID
 */
export const getPrintData = (taskId) => http.get(`/task/print-data/${taskId}`)

/**
 * 获取旧系统表单数据（兼容旧接口）
 * @param {Object} params - { tid: taskId, pid: stepId, flowGuid, formMode, nodeId }
 */
export const getOldFormData = (params) => {
  // 兼容旧系统接口：yxsoftFormHandler.ashx?req=basicinfo 和 req=getdata
  return http.get('/task/old/formdata', { params })
}

/**
 * 获取任务审批路线
 * @param {string} taskId - 任务ID
 */
export const getApprovalRoute = (taskId) => {
  const url = `${cloudWebBaseURL}/Ashx/G1WebApiHandler.ashx?action=approvalRoute`
  
  return http.get(url, {
    params: { tid: taskId },
    baseURL: ''
  })
}

/**
 * 获取表单基本信息（参考旧系统FormHandler API）
 * 调用 CloudWeb 的 G1WebApiHandler.ashx
 * @param {Object} params - { pid, tid, flowGuid, formMode }
 */
export const getFormBasicInfo = (params) => {
  const url = `${cloudWebBaseURL}/Ashx/G1WebApiHandler.ashx?action=formBasicInfo`
  
  return http.get(url, {
    params: {
      ...params,
      timestamp: Date.now(),
      _: Date.now()
    },
    baseURL: ''
  })
}

/**
 * 获取完整任务数据（包括表单数据、审批历史等）
 * 参考旧系统: yxsoftFormHandler.ashx?req=getdata
 * 调用 CloudWeb 的 G1WebApiHandler.ashx
 * @param {Object} params - { pid, tid, flowGuid, formMode, nodeId }
 */
export const getTaskCompleteData = (params) => {
  const url = `${cloudWebBaseURL}/Ashx/G1WebApiHandler.ashx?action=formData`
  
  return http.get(url, {
    params: {
      ...params,
      timestamp: Date.now(),
      _: Date.now()
    },
    baseURL: ''
  })
}
